import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listAdrenalin, getAdrenalinPocty, type AdrenalinPolozka,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Badge } from "../ui/Badge.js";

/**
 * ADRENALIN-OIL — FunnelWeb filtry + řídítka ACTION POWER.
 *
 * Rozsah rozhodl Vít 18. 8. 2026: *„chci jen ktm věci"* → 13 filtrů, které
 * podle VÝROBCE sednou na KTM, + 4 řídítka.
 *
 * 💰 Cena = PULTOVKA DODAVATELE (Vít: *„nás zajímá prodejní cena, ať sedí
 * s tím, co tam má dodavatel"*), ruční přepis vyhrává.
 * ⛔ NE cena z ABRY — u řídítek byla naše karta o 6,5–6,9 % dráž než dodavatel
 * na svém webu a zákazník si to vygoogluje.
 *
 * 🔑 „Sedne na" je fitment OD VÝROBCE (funnelwebfilter.com), ne z popisu
 * dodavatele — ten je špatně u 5 z 13 filtrů.
 */

const kc = (v: string | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

type Tone = "neutral" | "orange" | "green" | "amber" | "red" | "blue";

const STAV: Record<AdrenalinPolozka["eshopStav"], { text: string; tone: Tone }> = {
  "na-eshopu": { text: "Na eshopu", tone: "green" },
  ceka: { text: "Čeká na robota", tone: "amber" },
  chyba: { text: "Chyba", tone: "red" },
  skryto: { text: "Skryto", tone: "neutral" },
  mimo: { text: "Není na eshopu", tone: "neutral" },
};

function Karta({ p }: { p: AdrenalinPolozka }): JSX.Element {
  const s = STAV[p.eshopStav];
  return (
    <Link
      to={`/adrenalin/${encodeURIComponent(p.supplierId)}`}
      className="group flex flex-col rounded-2xl border border-zinc-200 overflow-hidden hover:border-ktm-orange transition-colors"
    >
      <div className="aspect-square bg-zinc-50 grid place-items-center overflow-hidden">
        {p.fotky[0]
          ? <img src={p.fotky[0]} alt={p.nazev}
                 className="w-full h-full object-contain p-5 group-hover:scale-105 transition-transform" />
          : <span className="text-zinc-300 text-sm">bez fotky</span>}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <Badge tone={s.tone}>{s.text}</Badge>
          {p.ohyby.length > 1 && (
            <span className="text-[11px] text-zinc-500">{p.ohyby.length}× ohyb</span>
          )}
        </div>
        <div className="text-[11px] text-zinc-500">
          {p.znacka} · {p.kod ?? "bez kódu"}
        </div>
        <div className="mt-0.5 text-sm font-medium leading-snug line-clamp-2">{p.nazev}</div>
        <div className="mt-1 text-[11px] text-zinc-500">
          {p.druh === "riditka"
            ? "univerzální"
            : p.fitmentModely.length > 0
              ? `${p.fitmentModely.length} modelů KTM` +
                (p.fitmentRoky.length
                  ? ` · ${p.fitmentRoky[0]}–${p.fitmentRoky[p.fitmentRoky.length - 1]}`
                  : "")
              : "fitment neznáme"}
        </div>
        <div className="mt-auto pt-2 flex items-baseline justify-between">
          <span className="text-base font-semibold tabular-nums">
            {kc(p.cena)}
            {p.cenaPrepsana && <span className="ml-1 text-amber-600" title="ručně přepsáno">✎</span>}
          </span>
          <span className={`text-xs ${p.skladDodavatele > 0 ? "text-green-700" : "text-red-600"}`}>
            {p.skladDodavatele > 0
              ? `${p.skladVice ? "> " : ""}${p.skladDodavatele} ks`
              : "nemá"}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function AdrenalinPage(): JSX.Element {
  const [polozky, setPolozky] = useState<AdrenalinPolozka[] | null>(null);
  const [pocty, setPocty] = useState<Awaited<ReturnType<typeof getAdrenalinPocty>> | null>(null);
  const [druh, setDruh] = useState<"" | "filtr" | "riditka">("");
  const [znacka, setZnacka] = useState("");
  const [jenSkladem, setJenSkladem] = useState(false);
  const [q, setQ] = useState("");
  const [chyba, setChyba] = useState<string | null>(null);

  const nacti = useCallback(async () => {
    try {
      const params: Parameters<typeof listAdrenalin>[0] = { jenKtm: true };
      if (druh) params.druh = druh;
      if (znacka) params.znacka = znacka;
      if (jenSkladem) params.jenSkladem = true;
      if (q.trim()) params.q = q.trim();
      const [d, c] = await Promise.all([listAdrenalin(params), getAdrenalinPocty()]);
      setPolozky(d.polozky);
      setPocty(c);
    } catch (e) {
      setChyba(e instanceof Error ? e.message : String(e));
    }
  }, [druh, znacka, jenSkladem, q]);

  useEffect(() => {
    const t = setTimeout(() => void nacti(), q ? 250 : 0);   // debounce hledání
    return () => clearTimeout(t);
  }, [nacti, q]);

  if (polozky === null) return <LoadingBlock />;

  const naEshopu = polozky.filter((x) => x.eshopStav === "na-eshopu").length;
  const mimo = polozky.filter((x) => x.eshopStav === "mimo").length;
  const ceka = polozky.filter((x) => x.eshopStav === "ceka").length;

  return (
    // ⚠ `p-6` je POVINNÉ — <main> v App.tsx padding nedává, dělá si ho
    //   každá stránka sama (vzor: AzpneuPage). Bez něj se obsah lepí na kraj.
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">ADRENALIN-OIL — FunnelWeb a ACTION POWER</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Filtry FunnelWeb na KTM a řídítka ACTION POWER. Cena se zrcadlí z pultovky
          dodavatele — klikni na produkt a dá se přepsat ručně.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span>Na eshopu: <strong>{naEshopu}</strong></span>
        <span>Čeká na robota: <strong>{ceka}</strong></span>
        <span className={mimo > 0 ? "text-ktm-orange" : ""}>
          Mohli bychom pustit: <strong>{mimo}</strong>
        </span>
        {pocty && <span className="text-zinc-400">Dodavatel vede celkem: {pocty.vse}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: "" as const, label: "Vše", n: pocty?.vse },
          { id: "filtr" as const, label: "Filtry", n: pocty?.filtr },
          { id: "riditka" as const, label: "Řídítka", n: pocty?.riditka },
        ].map((f) => (
          <button key={f.id} type="button" onClick={() => setDruh(f.id)}
            className={`min-w-0 rounded-full border px-3 py-1 text-sm ${
              druh === f.id ? "border-ktm-orange bg-orange-50 text-ktm-orange"
                            : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
            {f.label} {f.n !== undefined && <span className="text-zinc-400">{f.n}</span>}
          </button>
        ))}

        <span className="mx-1 h-5 w-px bg-zinc-200" />

        {(pocty?.znacky ?? []).map((z) => (
          <button key={z.znacka} type="button"
            onClick={() => setZnacka(znacka === z.znacka ? "" : z.znacka)}
            className={`min-w-0 rounded-full border px-3 py-1 text-sm ${
              znacka === z.znacka ? "border-ktm-orange bg-orange-50 text-ktm-orange"
                                  : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
            {z.znacka}
          </button>
        ))}

        <label className="ml-1 flex items-center gap-1.5 text-sm text-zinc-600">
          <input type="checkbox" checked={jenSkladem}
                 onChange={(e) => setJenSkladem(e.target.checked)} />
          jen skladem u dodavatele
        </label>
      </div>

      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="název, kód výrobce, model motorky (250 EXC-F)…"
        className="w-full rounded-xl border border-zinc-200 px-4 py-2 text-sm focus:border-ktm-orange focus:outline-none"
      />

      {chyba && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{chyba}</div>
      )}

      {polozky.length === 0 ? (
        <EmptyState title="Nic tu není"
                    description="Sběr ještě neproběhl, nebo filtr nic nenašel." />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {polozky.map((p) => <Karta key={p.supplierId} p={p} />)}
        </div>
      )}
    </div>
  );
}
